/**
 * VoiceNoteButton — record audio in the browser, upload to /voice-note,
 * which transcribes via Whisper and logs to the lead timeline.
 */
import { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { api } from "../lib/api";

export default function VoiceNoteButton({ leadId, onSaved }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const chunks = useRef([]);
  const timerRef = useRef(null);
  const startedAt = useRef(0);

  const start = async () => {
    setErr("");
    if (!navigator.mediaDevices?.getUserMedia) { setErr("Mic not available"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        const dur = Math.round((Date.now() - startedAt.current) / 1000);
        await upload(blob, dur);
      };
      mediaRef.current = rec;
      rec.start();
      startedAt.current = Date.now();
      setRecording(true);
      timerRef.current = setInterval(() => setElapsed(Math.round((Date.now() - startedAt.current) / 1000)), 250);
    } catch (e) {
      setErr("Mic permission denied");
    }
  };
  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    setRecording(false);
    setElapsed(0);
  };
  const upload = async (blob, durationSec) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("audio", blob, "voice.webm");
      form.append("duration_sec", String(durationSec));
      await api.post(`/dealer/lead/${leadId}/voice-note`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSaved?.();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="inline-flex items-center gap-2">
      {!recording ? (
        <button onClick={start} disabled={busy}
                className="text-[11px] font-extrabold text-white bg-purple-600 hover:bg-purple-700 px-2.5 py-1.5 rounded-full inline-flex items-center gap-1"
                data-testid="voice-note-start">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Mic size={11} />}
          {busy ? "Transcribing…" : "Voice note"}
        </button>
      ) : (
        <button onClick={stop}
                className="text-[11px] font-extrabold text-white bg-red-600 hover:bg-red-700 px-2.5 py-1.5 rounded-full inline-flex items-center gap-1 animate-pulse"
                data-testid="voice-note-stop">
          <Square size={11} fill="currentColor" /> Stop ({elapsed}s)
        </button>
      )}
      {err && <span className="text-[10px] text-red-600 font-bold">{err}</span>}
    </div>
  );
}
